import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, QueryList, ViewChildren } from "@angular/core";
import { List } from "immutable";
import { timer } from "rxjs";
import { takeWhile } from "rxjs/operators";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { ListItem } from "../../../../shared/list/list-item";
import { IOnUploadNew, UploadNewComponent } from "../../../../shared/upload/upload-new.component";
import { BulkProjectValidationStatus, IBulkProjectRow, IBulkProjectValidationResult } from "./bulk-project-response.model";
import { BulkProjectUploadService } from "./bulk-project-upload.service";

const BULK_PROJECT_FILE_TYPE = "BulkProject";
const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const VALIDATION_POLL_INTERVAL_MS = 2000;

@Component({
  selector: "app-bulk-project-upload",
  templateUrl: "./bulk-project-upload.component.html",
  styleUrls: ["./bulk-project-upload.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkProjectUploadComponent implements OnDestroy {

  fileName: string;
  fileLocation: string;
  loadId: string;
  isFileUploaded = false;
  isValidated = false;
  isProjectValidationPassed = false;
  isProcessingVisible = false;
  processingHeaderText: string;
  validationRows: IBulkProjectRow[] = [];
  summaryReport = List<ListItem>();
  private isValidationComplete = false;
  private readonly subscriptions = new SubSink();
  @ViewChildren("appFileUpload") filesToUpload: QueryList<UploadNewComponent>;

  constructor(
    private bulkProjectUploadService: BulkProjectUploadService,
    private messagingService: MessagingService,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get summaryHeader(): string {
    return "VALIDATION SUMMARY";
  }

  get validationStatusMessage(): string {
    return this.isProjectValidationPassed ? "PASSED" : "FAILED";
  }

  get hasPassed(): boolean {
    return this.isProjectValidationPassed;
  }

  trackByIndex(index) {
    return index;
  }

  uploadDocument(event: IOnUploadNew): void {
    const file = event.formData.getAll("Document")[0] as File;

    if (!file) {
      return;
    }

    this.processingHeaderText = "PLEASE WAIT...UPLOAD IS IN PROGRESS";
    this.isProcessingVisible = true;
    this.changeDetector.markForCheck();

    const payload = {
      name: file.name,
      type: file.type || XLSX_MIME_TYPE,
      fileType: BULK_PROJECT_FILE_TYPE,
    };

    this.bulkProjectUploadService.getPresignedURL(JSON.stringify(payload)).subscribe({
      next: response => {
        if (response && response.presignedURL) {
          this.bulkProjectUploadService.uploadFile(response.presignedURL, file)
            .then(() => {
              this.fileName = file.name;
              this.fileLocation = response.fileName;
              this.isFileUploaded = true;
              this.isProcessingVisible = false;
              this.messagingService.showToast("File uploaded successfully.", SeverityType.SUCCESS);
              this.changeDetector.markForCheck();
            })
            .catch(() => {
              this.onUploadError();
            });
        } else {
          this.onUploadError();
        }
      },
      error: () => {
        this.onUploadError();
      },
    });
  }

  validate(): void {
    if (!this.isFileUploaded) {
      this.messagingService.showToast("Please upload an .xlsx file.", SeverityType.ERROR);
      return;
    }

    this.isValidated = false;
    this.isValidationComplete = false;
    this.processingHeaderText = "PLEASE WAIT...VALIDATION IS IN PROGRESS";
    this.isProcessingVisible = true;

    this.subscriptions.sink = this.bulkProjectUploadService.startValidation(this.fileLocation).subscribe(
      result => {
        this.loadId = result.loadId;
        this.handleValidationResult(result);

        if (!this.isValidationComplete) {
          this.pollValidationStatus();
        }
      },
      () => this.onValidationError());
  }

  reset(): void {
    this.filesToUpload.forEach(fileToUpload => {
      fileToUpload.clearUpload();
    });
    this.fileName = null;
    this.fileLocation = null;
    this.loadId = null;
    this.isFileUploaded = false;
    this.isValidated = false;
    this.isValidationComplete = true;
    this.isProjectValidationPassed = false;
    this.validationRows = [];
    this.summaryReport = List<ListItem>();
    this.changeDetector.markForCheck();
  }

  downloadValidationIssues(): void {
    const failedRows = this.validationRows.filter(row => !row.isValid);
    const header = ["Row Number", "Project Name", "Project Type", "Failed Reason"];
    const csvRows = failedRows.map(row => [
      row.rowNumber,
      this.escapeCsv(row.projectName),
      this.escapeCsv(row.projectType),
      this.escapeCsv(row.failedReason),
    ].join(","));
    const csv = [header.join(","), ...csvRows].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "validation-issues.csv";
    a.click();
    a.remove();
  }

  getValidationMessageClass(value: string): string {
    switch (value) {
      case "FAILED":
        return "summary-body__details--danger";
      case "PASSED":
        return "summary-body__details--pass";
      default:
        return "summary-body__details";
    }
  }

  private pollValidationStatus(): void {
    this.subscriptions.sink = timer(VALIDATION_POLL_INTERVAL_MS, VALIDATION_POLL_INTERVAL_MS)
      .pipe(takeWhile(() => !this.isValidationComplete))
      .subscribe(() => {
        this.bulkProjectUploadService.getValidationStatus(this.loadId).subscribe(
          result => this.handleValidationResult(result),
          () => this.onValidationError());
      });
  }

  private handleValidationResult(result: IBulkProjectValidationResult): void {
    if (!result) {
      return;
    }

    const status = (result.status || "").toUpperCase();

    if (status === BulkProjectValidationStatus.Failed.toUpperCase()) {
      this.isValidationComplete = true;
      this.isProcessingVisible = false;
      this.messagingService.showToast(result.errorMessage || "Validation failed. Please try again.", SeverityType.ERROR);
      this.changeDetector.markForCheck();
      return;
    }

    if (status === BulkProjectValidationStatus.ValidationComplete.toUpperCase()
      || status === BulkProjectValidationStatus.Completed.toUpperCase()) {
      this.isValidationComplete = true;
      this.isProjectValidationPassed = result.totalFailed === 0;
      this.validationRows = result.rows || [];

      this.summaryReport = List<ListItem>();
      this.summaryReport = this.summaryReport.push(
        new ListItem({ key: "Total Projects", value: result.totalRequested.toString(), url: "" }),
        new ListItem({ key: "Total Projects Passed", value: result.totalPassed.toString(), url: "" }),
        new ListItem({ key: "Total Projects Failed", value: result.totalFailed.toString(), url: "" }),
        new ListItem({ key: "Validation Status", value: this.validationStatusMessage, url: "" })
      );

      this.isValidated = true;
      this.isProcessingVisible = false;
      this.changeDetector.markForCheck();
    }
  }

  private onValidationError(): void {
    this.isValidationComplete = true;
    this.isProcessingVisible = false;
    this.messagingService.showToast("Please try again", SeverityType.ERROR);
    this.changeDetector.markForCheck();
  }

  private onUploadError(): void {
    this.isProcessingVisible = false;
    this.messagingService.showToast("Error uploading file using pre-signed URL, please try again.", SeverityType.ERROR);
    this.reset();
  }

  private escapeCsv(value: string): string {
    const safe = (value || "").replace(/"/g, '""');
    return `"${safe}"`;
  }
}
